// Shared validation for DataRequestField values. Used by the milestone
// Submit handler (and individual renderers, optionally) so client-side
// rejection of malformed input matches what the backend will reject.
// Pure — no React, no DOM.

import { DataRequestField, ExpectationRule } from '../@types';

export type FieldValidationError = string | null;

const isEmpty = (v: unknown): boolean => {
	if (v == null) return true;
	if (typeof v === 'string') return v.trim() === '';
	if (Array.isArray(v)) return v.length === 0;
	return false;
};

const ruleArray = (
	rule: DataRequestField['expectedResponse']
): ExpectationRule[] => {
	if (rule == null) return [];
	if (typeof rule === 'string') return [];
	if (Array.isArray(rule)) return rule;
	return [rule];
};

const compareNumber = (
	value: number,
	rule: ExpectationRule
): FieldValidationError => {
	const target = typeof rule.value === 'number' ? rule.value : Number(rule.value);
	const secondary = rule.secondaryValue != null ? Number(rule.secondaryValue) : null;
	switch (rule.operator) {
		case 'eq':
			if (value !== target) return rule.message || `Must equal ${target}`;
			return null;
		case 'neq':
			if (value === target) return rule.message || `Must not equal ${target}`;
			return null;
		case 'gt':
			if (!(value > target)) return rule.message || `Must be greater than ${target}`;
			return null;
		case 'gte':
			if (!(value >= target)) return rule.message || `Must be at least ${target}`;
			return null;
		case 'lt':
			if (!(value < target)) return rule.message || `Must be less than ${target}`;
			return null;
		case 'lte':
			if (!(value <= target)) return rule.message || `Must be at most ${target}`;
			return null;
		case 'between':
			if (secondary != null && (value < target || value > secondary)) {
				return rule.message || `Must be between ${target} and ${secondary}`;
			}
			return null;
		default:
			return null;
	}
};

export const validateField = (
	field: DataRequestField,
	value: unknown,
	uploadedDocumentIds?: string[]
): FieldValidationError => {
	const required = field.required === true;

	if (field.fieldType === 'document') {
		const count = uploadedDocumentIds?.length ?? 0;
		if (required && count === 0) {
			return `${field.label} requires at least one document`;
		}
		if (field.maxDocuments != null && count > field.maxDocuments) {
			return `${field.label} accepts at most ${field.maxDocuments} document(s)`;
		}
		return null;
	}

	if (required && isEmpty(value)) {
		return `${field.label} is required`;
	}
	if (isEmpty(value)) return null;

	if (field.fieldType === 'number') {
		const num = typeof value === 'number' ? value : Number(value);
		if (Number.isNaN(num)) return `${field.label} must be a number`;
		for (const rule of ruleArray(field.expectedResponse)) {
			const err = compareNumber(num, rule);
			if (err) return err;
		}
		return null;
	}

	if (field.fieldType === 'yesno') {
		const v = String(value).toLowerCase();
		if (v !== 'yes' && v !== 'no' && v !== 'true' && v !== 'false') {
			return `${field.label} must be yes or no`;
		}
		const rules = ruleArray(field.expectedResponse);
		const normalised = v === 'yes' || v === 'true' ? 'yes' : 'no';
		for (const rule of rules) {
			if (rule.operator === 'eq' && rule.value != null) {
				const target = String(rule.value).toLowerCase();
				if (target !== normalised) {
					return rule.message || `${field.label} must be "${target}"`;
				}
			}
		}
		return null;
	}

	if (field.fieldType === 'select') {
		const allowed = field.options || [];
		if (allowed.length > 0 && !allowed.includes(String(value))) {
			return `${field.label} must be one of the listed options`;
		}
		return null;
	}

	return null;
};
